// Organisation picker after a Google sign-in that opens several organisations.
//
// The Google identity is already verified; the API holds it in a short-lived
// httpOnly cookie. Picking one finishes the login into that organisation — the
// others stay available in the org switcher.

import { useMutation, useQuery } from "@tanstack/react-query";

import { Brand } from "@/components/brand";
import { Link } from "@/components/ui/link";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";
import { getSsoPending, selectSsoOrganization } from "@/lib/api/endpoints";

import { useAfterLogin } from "../../login/page";

export default function SelectOrganizationPage() {
  const afterLogin = useAfterLogin();
  const pending = useQuery({ queryKey: ["sso-pending"], queryFn: getSsoPending, retry: false });
  const select = useMutation({
    mutationFn: selectSsoOrganization,
    onSuccess: afterLogin,
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <main className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Brand />
        </div>
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle>Choose an organisation</CardTitle>
            <CardDescription>
              {pending.data ? `Signed in with Google as ${pending.data.email}.` : " "}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {pending.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : pending.isError || !pending.data ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {pending.error instanceof Error
                    ? pending.error.message
                    : "Your Google sign-in has expired."}
                </p>
                <Button asChild variant="outline" className="w-full">
                  <Link href="/login">Back to sign in</Link>
                </Button>
              </div>
            ) : pending.data.choices.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                This Google account no longer has access to any organisation.
              </p>
            ) : (
              <div className="space-y-2">
                {pending.data.choices.map((org) => (
                  <Button
                    key={org.slug}
                    variant="outline"
                    className="w-full justify-between"
                    disabled={select.isPending}
                    onClick={() => select.mutate(org.slug)}
                  >
                    <span>{org.display_name}</span>
                    <span className="font-mono text-xs text-muted-foreground">{org.slug}</span>
                  </Button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
